import MakulaLogo from "@shared/svg/makula-logo.svg?react";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import AppModal from "@shared/ui/Modal";
import ToggleSwitch from "@shared/ui/ToggleSwitch";
import { Info } from "phosphor-react";
import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";
import QRCodeLib from "react-qr-code";
import { useReactToPrint } from "react-to-print";

const QRCode = (QRCodeLib as any)?.default ?? QRCodeLib;

export type QRCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  assetName: string;
  serialNumber: string;
  generalAccessUrl: string;
  oemLogo?: string;
  sharedBy?: string;
  sharedByLogo?: string;
};

const QRCodeModal = ({
  isOpen,
  onClose,
  assetName,
  serialNumber,
  generalAccessUrl,
  oemLogo = "",
  sharedBy = "",
  sharedByLogo = "",
}: QRCodeModalProps) => {
  const { messages } = useIntl();
  const qrCodeContainerRef = useRef<HTMLDivElement>(null);
  const [showNameAndNumber, setShowNameAndNumber] = useState(true);
  const [copied, setCopied] = useState(false);

  const handlePrint = useReactToPrint({
    contentRef: qrCodeContainerRef,
  });

  const logoSrc = sharedByLogo || oemLogo;

  return (
    <AppModal
      isOpen={isOpen}
      maxWidth="!max-w-[494px]"
      title={
        (messages as Record<string, unknown>)["hierarchy"]?.["assets"]?.[
          "useQrCode"
        ] as string
      }
      handleClose={(e) => {
        e.preventDefault();
        setShowNameAndNumber(true);
        onClose();
      }}
      content={
        <div className="facility-details-content-qr-code-container">
          <p className="helper-text info u-margin-b-5">
            <span className="icon">
              <Info size={16} />
            </span>
            {
              (messages as Record<string, unknown>)["hierarchy"]?.["assets"]?.[
                "qrCodeModalMessage"
              ] as string
            }
          </p>

          <div className="switcher-right u-flex u-items-center u-margin-b-4">
            <ToggleSwitch
              name="show-name-and-serial-number"
              isOn={showNameAndNumber}
              onChange={() => setShowNameAndNumber((prev) => !prev)}
            />
            <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="ml-md">
              {
                (messages as Record<string, unknown>)["hierarchy"]?.[
                  "assets"
                ]?.["qrCodeNameAndNumber"] as string
              }
            </BodyText>
          </div>

          {generalAccessUrl && (
            <div
              className={`qr-code-container flex ${
                showNameAndNumber ? "" : "justify-center"
              }`}
              ref={qrCodeContainerRef}
            >
              <div className="qr-code-wrapper relative">
                <QRCode
                  size={152}
                  value={generalAccessUrl}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
              {showNameAndNumber && (
                <div className="u-flex u-flex-column u-margin-l-3">
                  <div className="u-flex u-justify-between u-flex-column u-height-100">
                    <div>
                      <div className="mb-md">
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            className="w-4xl h-4xl self-center object-contain"
                          />
                        ) : (
                          <MakulaLogo className="w-4xl h-4xl" />
                        )}
                      </div>
                      {sharedBy && (
                        <div>
                          <BodyText
                            size={BODY_TEXT_SIZES.X_SMALL}
                            color="text-secondary"
                            className="mb-xs"
                          >
                            {
                              (messages as Record<string, unknown>)?.[
                                "sharedAssets"
                              ]?.["sharedBy"] as string
                            }
                          </BodyText>
                          <BodyText
                            size={BODY_TEXT_SIZES.X_SMALL}
                            className="break-words !font-bold"
                          >
                            {sharedBy}
                          </BodyText>
                        </div>
                      )}
                      <div className={`${sharedBy ? "mt-md" : ""}`}>
                        <BodyText
                          size={BODY_TEXT_SIZES.X_SMALL}
                          color="text-secondary"
                          className="mb-xs"
                        >
                          {
                            (messages as Record<string, unknown>)[
                              "hierarchy"
                            ]?.["assets"]?.["nameTextSingular"] as string
                          }
                        </BodyText>
                        <BodyText
                          size={BODY_TEXT_SIZES.X_SMALL}
                          className="break-words !font-bold"
                        >
                          {assetName}
                        </BodyText>
                      </div>
                      <div className="mt-xl">
                        <BodyText
                          size={BODY_TEXT_SIZES.X_SMALL}
                          color="text-secondary"
                          className="mb-xs"
                        >
                          {
                            (messages as Record<string, unknown>)?.[
                              "machines"
                            ]?.["machineDetails"]?.["detailsTab"]?.["qrCode"]?.[
                              "serialNumber"
                            ] as string
                          }
                        </BodyText>
                        <BodyText
                          size={BODY_TEXT_SIZES.X_SMALL}
                          className="break-words !font-bold"
                        >
                          {serialNumber}
                        </BodyText>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex items-center justify-end py-lg px-2xl border-solid border-t border-b-0 border-x-0 border-primary gap-sm">
          <Button
            variant={BUTTON_VARIANTS.OUTLINE}
            text={
              (messages as Record<string, unknown>)?.["machines"]?.[
                "machineDetails"
              ]?.["detailsTab"]?.["qrCode"]?.["printQRCode"] as string
            }
            className="mr-xs"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              handlePrint();
            }}
          />

          <Button
            text={
              copied
                ? ((messages as Record<string, unknown>)?.["machines"]?.[
                    "machineDetails"
                  ]?.["detailsTab"]?.["qrCode"]?.["copyTooltip"] as string)
                : ((messages as Record<string, unknown>)?.["machines"]?.[
                    "machineDetails"
                  ]?.["detailsTab"]?.["qrCode"]?.["copyUrl"] as string)
            }
            onClick={() => {
              navigator.clipboard.writeText(generalAccessUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 1000);
            }}
          />
        </div>
      }
    />
  );
};

export default QRCodeModal;
